import React from "react";
import { WidgetProps } from "@rjsf/utils";
import { Input, Tooltip } from "antd";
import { CheckCircleTwoTone, ExclamationCircleTwoTone } from "@ant-design/icons";
import Image from "next/image";
import { getThresholdValue } from "../../../../utils/objectUtils";

const InputWithUnitWidget = (props: WidgetProps) => {
    const {
        id,
        value,
        disabled,
        readonly,
        placeholder,
        onChange,
        onBlur,
        onFocus,
        options,
        schema,
    } = props;

    const warning = schema.backgroundData?.warning;
    const projectType = options?.projectType;
    
    // Use utility function to get threshold with proper fallback logic
    const numericThreshold = getThresholdValue(
        schema.backgroundData, 
        typeof projectType === 'string' ? projectType : undefined
    );
    
    const unit = options?.unit;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value === "" ? undefined : e.target.value;
        onChange(newValue);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        if (onBlur) {
            onBlur(id, e.target.value);
        }
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        if (onFocus) {
            onFocus(id, e.target.value);
        }
    };

    // Determine input type based on schema type
    const getInputType = () => {
        if (schema?.type === "number" || schema?.type === "integer") {
            return "number";
        }
        return "text";
    };

    return (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {readonly || disabled ? (
                <>
                    <span className={`text-xs flex flex-row justify start text-base ${disabled ? 'text-gray-500' : 'text-primary-800'}`}>{value}</span>
                    <span className="hidden">
                        <Input
                            id={id}
                            type={getInputType()}
                            value={value || ""}
                            disabled={disabled}
                            readOnly={readonly}
                            placeholder={placeholder ? placeholder : "Geef de hoeveelheid"}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            onFocus={handleFocus}
                        />
                    </span>
                </>
            ) : (
                <Tooltip title={String(unit)} color="var(--primary-100)" style={{ cursor: "help" }}>

                    <div className="flex justify-start w-full gap-1 items-center">
                        {schema.icon && (
                            <div className="flex-none w-1/5 flex justify-center">
                                <div className="rounded p-1 flex justify-center items-center w-7 h-7 bg-primary_900">
                                    <div className='svg-white'>
                                        <Image
                                            src={`/icons/${schema.icon}`}
                                            alt={`${schema.icon} image`}
                                            width={28}
                                            height={28}
                                        // className="rounded p-1 font-bold svg-white"
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                        <div className={`relative flex flex-row items-center ${schema.icon ? 'w-3/5' : 'w-full'}`}>
                            <Input
                                id={id}
                                type={getInputType()}
                                value={value || ""}
                                disabled={disabled}
                                readOnly={readonly}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                onFocus={handleFocus}
                                style={readonly ? { borderWidth: '1px', borderColor: 'transparent', backgroundColor: 'transparent', width: '100%' } : { width: '100%' }}
                            />
                            {schema.backgroundData && numericThreshold !== undefined && !isNaN(numericThreshold) && (
                                <span className="absolute right-1 top-[50%] z-[100] -translate-y-1/2">
                                    {(warning && parseFloat(value) > numericThreshold) || (!warning && parseFloat(value) <= numericThreshold) ?
                                        <ExclamationCircleTwoTone twoToneColor="#d78d17ff" /> :
                                        <CheckCircleTwoTone twoToneColor="#52c41a" />
                                    }
                                </span>
                            )}
                        </div>
                        {unit && (
                            <div className="flex justify-start pl-2" title={String(unit)} style={{ maxWidth: "50%" }}>
                                <span style={{
                                    fontSize: "10px",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                }}>
                                    {String(unit)}
                                </span>
                            </div>
                        )}
                    </div>
                </Tooltip>
            )}
        </div>
    );
};

export default InputWithUnitWidget;
